import type { Components, Theme, CSSObject } from "@mui/material/styles";
import type { SemanticPalette } from "../../tokens/colorSemantic";
import { designShadows } from "../../tokens/shadows";
import { type StateSpec, stateToCss } from "../helpers";
import { RadiusToken } from "../../tokens/radii";

type Rmh = SemanticPalette;

type ChipColorVariantSpec = {
  bordered: boolean;
  textColor: string;
  enabled: StateSpec;
  hovered: StateSpec;
  focused: StateSpec;
  pressed: StateSpec;
  disabled: StateSpec;
};

const PRESSED_ELEVATION = designShadows[1];
const DISABLED_OPACITY = 0.38;

const MUI_SELECTORS = {
  hovered: "&:hover:not(.Mui-disabled)",
  focused: "&.Mui-focusVisible",
  pressed: "&:active:not(.Mui-disabled)",
  disabled: "&.Mui-disabled",
};

const colorStyle =
  (build: (rmh: Rmh) => ChipColorVariantSpec) =>
  ({ theme }: { theme: Theme }): CSSObject => {
    const spec = build(theme.vars!.palette.rmh);
    return {
      borderWidth: spec.bordered ? "1px" : 0,
      color: spec.textColor,
      ...stateToCss(spec.enabled),
      [MUI_SELECTORS.hovered]: stateToCss(spec.hovered),
      [MUI_SELECTORS.focused]: stateToCss(spec.focused),
      [MUI_SELECTORS.pressed]: stateToCss(spec.pressed),
      [MUI_SELECTORS.disabled]: stateToCss(spec.disabled),
    };
  };

// --- spec factories --------------------------------------------------------

// Default
const defaultFilled = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: false,
  textColor: rmh.text.primary,
  enabled: { backgroundColor: rmh.paper["4-faint"].rest },
  hovered: { backgroundColor: rmh.paper["4-faint"].hover },
  focused: {
    backgroundColor: rmh.paper["4-faint"].rest,
    ring: rmh.focus.neutral,
  },
  pressed: {
    backgroundColor: rmh.paper["4-faint"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["4-faint"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const defaultMuted = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.primary,
  enabled: {
    backgroundColor: rmh.paper["3-muted"].rest,
    borderColor: rmh.border["3-muted"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["3-muted"].hover,
    borderColor: rmh.border["3-muted"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["3-muted"].rest,
    borderColor: rmh.border["3-muted"].hover,
    ring: rmh.focus.neutral,
  },
  pressed: {
    backgroundColor: rmh.paper["3-muted"].active,
    borderColor: rmh.border["3-muted"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["3-muted"].rest,
    borderColor: rmh.border["1-surface"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const defaultOutlined = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.primary,
  enabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["1-surface"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["1-surface"].hover,
    borderColor: rmh.border["1-surface"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["1-surface"].hover,
    ring: rmh.focus.neutral,
  },
  pressed: {
    backgroundColor: rmh.paper["1-surface"].active,
    borderColor: rmh.border["1-surface"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["1-surface"].rest,
    opacity: DISABLED_OPACITY,
  },
});

// Primary
const primaryFilled = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: false,
  textColor: rmh.text.staticWhite,
  enabled: { backgroundColor: rmh.paper["5-brandFilled"].rest },
  hovered: { backgroundColor: rmh.paper["5-brandFilled"].hover },
  focused: {
    backgroundColor: rmh.paper["5-brandFilled"].rest,
    ring: rmh.focus.primary,
  },
  pressed: {
    backgroundColor: rmh.paper["5-brandFilled"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["5-brandFilled"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const primaryMuted = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.primary,
  enabled: {
    backgroundColor: rmh.paper["7-brandFaint"].rest,
    borderColor: rmh.border["6-brandMuted"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["7-brandFaint"].hover,
    borderColor: rmh.border["6-brandMuted"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["7-brandFaint"].rest,
    borderColor: rmh.border["6-brandMuted"].hover,
    ring: rmh.focus.primary,
  },
  pressed: {
    backgroundColor: rmh.paper["7-brandFaint"].active,
    borderColor: rmh.border["6-brandMuted"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["7-brandFaint"].rest,
    borderColor: rmh.border["6-brandMuted"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const primaryOutlined = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.brand,
  enabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["5-brand"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["7-brandFaint"].hover,
    borderColor: rmh.border["5-brand"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["5-brand"].hover,
    ring: rmh.focus.primary,
  },
  pressed: {
    backgroundColor: rmh.paper["7-brandFaint"].active,
    borderColor: rmh.border["5-brand"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["5-brand"].rest,
    opacity: DISABLED_OPACITY,
  },
});

// Error
const errorFilled = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: false,
  textColor: rmh.text.staticWhite,
  enabled: { backgroundColor: rmh.paper["8-errorFilled"].rest },
  hovered: { backgroundColor: rmh.paper["8-errorFilled"].hover },
  focused: {
    backgroundColor: rmh.paper["8-errorFilled"].hover,
    ring: rmh.focus.error,
  },
  pressed: {
    backgroundColor: rmh.paper["8-errorFilled"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["8-errorFilled"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const errorMuted = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.primary,
  enabled: {
    backgroundColor: rmh.paper["9-errorFaint"].rest,
    borderColor: rmh.border["9-errorFaint"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["9-errorFaint"].hover,
    borderColor: rmh.border["9-errorFaint"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["9-errorFaint"].rest,
    borderColor: rmh.border["9-errorFaint"].hover,
    ring: rmh.focus.error,
  },
  pressed: {
    backgroundColor: rmh.paper["9-errorFaint"].active,
    borderColor: rmh.border["9-errorFaint"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["9-errorFaint"].rest,
    borderColor: rmh.border["9-errorFaint"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const errorOutlined = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.error,
  enabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["8-error"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["9-errorFaint"].hover,
    borderColor: rmh.border["8-error"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["8-error"].hover,
    ring: rmh.focus.error,
  },
  pressed: {
    backgroundColor: rmh.paper["9-errorFaint"].active,
    borderColor: rmh.border["8-error"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["8-error"].rest,
    opacity: DISABLED_OPACITY,
  },
});

// Warning
const warningFilled = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: false,
  textColor: rmh.text.primary,
  enabled: { backgroundColor: rmh.paper["12-warningFilled"].rest },
  hovered: { backgroundColor: rmh.paper["12-warningFilled"].hover },
  focused: {
    backgroundColor: rmh.paper["12-warningFilled"].hover,
    ring: rmh.focus.warning,
  },
  pressed: {
    backgroundColor: rmh.paper["12-warningFilled"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["12-warningFilled"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const warningMuted = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.primary,
  enabled: {
    backgroundColor: rmh.paper["13-warningFaint"].rest,
    borderColor: rmh.border["13-warningFaint"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["13-warningFaint"].hover,
    borderColor: rmh.border["13-warningFaint"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["13-warningFaint"].rest,
    borderColor: rmh.border["13-warningFaint"].hover,
    ring: rmh.focus.warning,
  },
  pressed: {
    backgroundColor: rmh.paper["13-warningFaint"].active,
    borderColor: rmh.border["13-warningFaint"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["13-warningFaint"].rest,
    borderColor: rmh.border["13-warningFaint"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const warningOutlined = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.warning,
  enabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["12-warning"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["13-warningFaint"].hover,
    borderColor: rmh.border["12-warning"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["12-warning"].hover,
    ring: rmh.focus.warning,
  },
  pressed: {
    backgroundColor: rmh.paper["13-warningFaint"].active,
    borderColor: rmh.border["12-warning"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["12-warning"].rest,
    opacity: DISABLED_OPACITY,
  },
});

// Success
const successFilled = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: false,
  textColor: rmh.text.staticWhite,
  enabled: { backgroundColor: rmh.paper["10-successFilled"].rest },
  hovered: { backgroundColor: rmh.paper["10-successFilled"].hover },
  focused: {
    backgroundColor: rmh.paper["10-successFilled"].hover,
    ring: rmh.focus.success,
  },
  pressed: {
    backgroundColor: rmh.paper["10-successFilled"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["10-successFilled"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const successMuted = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.primary,
  enabled: {
    backgroundColor: rmh.paper["11-successFaint"].rest,
    borderColor: rmh.border["11-successFaint"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["11-successFaint"].hover,
    borderColor: rmh.border["11-successFaint"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["11-successFaint"].hover,
    borderColor: rmh.border["11-successFaint"].hover,
    ring: rmh.focus.success,
  },
  pressed: {
    backgroundColor: rmh.paper["11-successFaint"].active,
    borderColor: rmh.border["11-successFaint"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: rmh.paper["11-successFaint"].rest,
    borderColor: rmh.border["11-successFaint"].rest,
    opacity: DISABLED_OPACITY,
  },
});

const successOutlined = (rmh: Rmh): ChipColorVariantSpec => ({
  bordered: true,
  textColor: rmh.text.success,
  enabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["10-success"].rest,
  },
  hovered: {
    backgroundColor: rmh.paper["11-successFaint"].hover,
    borderColor: rmh.border["10-success"].hover,
  },
  focused: {
    backgroundColor: rmh.paper["1-surface"].rest,
    borderColor: rmh.border["10-success"].hover,
    ring: rmh.focus.success,
  },
  pressed: {
    backgroundColor: rmh.paper["11-successFaint"].active,
    borderColor: rmh.border["10-success"].active,
    elevation: PRESSED_ELEVATION,
  },
  disabled: {
    backgroundColor: "transparent",
    borderColor: rmh.border["10-success"].rest,
    opacity: DISABLED_OPACITY,
  },
});

export const MuiChip: NonNullable<Components<Theme>["MuiChip"]> = {
  defaultProps: {
    variant: "filled",
    color: "default",
    size: "medium",
  },
  styleOverrides: {
    root: ({ theme }) => ({
      borderRadius: RadiusToken.MD,
      borderStyle: "solid",
      borderWidth: 0,
      borderColor: "transparent",
      ...theme.typography.bodySmMedium,
      transition: theme.transitions.create(
        ["background-color", "border-color", "box-shadow", "opacity", "color"],
        { duration: theme.transitions.duration.shortest }
      ),
      // Chip has no `disableRipple` prop, so suppress the ButtonBase ripple via CSS.
      "& .MuiTouchRipple-root": { display: "none" },
    }),
  },
  variants: [
    // sizes
    {
      props: { size: "small" },
      style: ({ theme }: { theme: Theme }) => ({
        minHeight: "1.5rem",
        maxHeight: "1.5rem",
        padding: `${theme.spacing(0.75)} ${theme.spacing(1)}`,
      }),
    },
    {
      props: { size: "medium" },
      style: ({ theme }: { theme: Theme }) => ({
        padding: theme.spacing(1),
      }),
    },
    // default
    {
      props: { color: "default", variant: "filled" },
      style: colorStyle(defaultFilled),
    },
    {
      props: { color: "default", variant: "muted" },
      style: colorStyle(defaultMuted),
    },
    {
      props: { color: "default", variant: "outlined" },
      style: colorStyle(defaultOutlined),
    },
    // primary
    {
      props: { color: "primary", variant: "filled" },
      style: colorStyle(primaryFilled),
    },
    {
      props: { color: "primary", variant: "muted" },
      style: colorStyle(primaryMuted),
    },
    {
      props: { color: "primary", variant: "outlined" },
      style: colorStyle(primaryOutlined),
    },
    // error
    {
      props: { color: "error", variant: "filled" },
      style: colorStyle(errorFilled),
    },
    {
      props: { color: "error", variant: "muted" },
      style: colorStyle(errorMuted),
    },
    {
      props: { color: "error", variant: "outlined" },
      style: colorStyle(errorOutlined),
    },
    // warning
    {
      props: { color: "warning", variant: "filled" },
      style: colorStyle(warningFilled),
    },
    {
      props: { color: "warning", variant: "muted" },
      style: colorStyle(warningMuted),
    },
    {
      props: { color: "warning", variant: "outlined" },
      style: colorStyle(warningOutlined),
    },
    // success
    {
      props: { color: "success", variant: "filled" },
      style: colorStyle(successFilled),
    },
    {
      props: { color: "success", variant: "muted" },
      style: colorStyle(successMuted),
    },
    {
      props: { color: "success", variant: "outlined" },
      style: colorStyle(successOutlined),
    },
  ],
};
